import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import { jobService } from '../services/jobService';

const JOB_STATUSES = [
  { key: 'Quoted',      label: 'Quoted',      color: 'border-blue-400',   bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-700 dark:text-blue-300',   badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  { key: 'In Progress', label: 'In Progress', color: 'border-amber-400',  bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  { key: 'Completed',   label: 'Completed',   color: 'border-green-400',  bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  { key: 'Cancelled',   label: 'Cancelled',   color: 'border-gray-400',   bg: 'bg-gray-50 dark:bg-gray-700/30',   text: 'text-gray-600 dark:text-gray-400',   badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
];

const formatGBP = (val) =>
  `£${parseFloat(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobStats, setJobStats] = useState(null);

  useEffect(() => {
    jobService.getJobs().then((data) => {
      const jobs = data.data || [];
      const stats = {};
      JOB_STATUSES.forEach(({ key }) => {
        stats[key] = { count: 0, net: 0, gross: 0 };
      });
      jobs.forEach((job) => {
        const s = job.status || 'Quoted';
        if (!stats[s]) return;
        const net = parseFloat(job.value) || 0;
        const rate = job.vat_rate ? parseFloat(job.vat_rate.rate) : 0;
        stats[s].count += 1;
        stats[s].net += net;
        stats[s].gross += net * (1 + rate / 100);
      });
      setJobStats(stats);
    }).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1 className="text-gray-900 dark:text-white text-xl sm:text-2xl font-bold">Dashboard</h1>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">
                Welcome, {user?.name}!
              </span>
              <button 
                onClick={handleLogout} 
                className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600 hover:transform hover:-translate-y-0.5"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2.5 py-10">
        <div className="grid gap-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700">
            <h2 className="text-gray-900 dark:text-white text-3xl font-bold mb-3">🎉 Welcome to Your Dashboard!</h2>
            <p className="text-gray-600 dark:text-gray-300 text-base mb-8">You have successfully logged in to your account.</p>
            
            {jobStats && (
              <div className="mb-8">
                <h3 className="text-gray-800 dark:text-white text-xl font-semibold mb-4 pb-3 border-b-2 border-gray-200 dark:border-gray-700">Job Summary</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {JOB_STATUSES.map(({ key, label, color, bg, text, badge }) => {
                    const s = jobStats[key];
                    return (
                      <div key={key} className={`rounded-xl border-l-4 ${color} ${bg} p-4 flex flex-col gap-2`}>
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge}`}>{label}</span>
                          <span className={`text-2xl font-bold ${text}`}>{s.count}</span>
                        </div>
                        <div className="mt-1 space-y-0.5">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Net</p>
                          <p className={`text-sm font-semibold ${text}`}>{formatGBP(s.net)}</p>
                          {s.gross !== s.net && (
                            <>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Inc VAT</p>
                              <p className={`text-sm font-semibold ${text}`}>{formatGBP(s.gross)}</p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mb-8">
              <h3 className="text-gray-800 dark:text-white text-xl font-semibold mb-4 pb-3 border-b-2 border-gray-200 dark:border-gray-700">Account Information</h3>
              <div className="grid gap-4 mt-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 border-blue-500">
                  <strong className="text-gray-800 dark:text-white sm:w-32 font-semibold text-sm sm:text-base">Name:</strong>
                  <span className="text-gray-600 dark:text-gray-300">{user?.name}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 border-blue-500">
                  <strong className="text-gray-800 dark:text-white sm:w-32 font-semibold text-sm sm:text-base">Email:</strong>
                  <span className="text-gray-600 dark:text-gray-300">{user?.email}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 border-blue-500">
                  <strong className="text-gray-800 dark:text-white sm:w-32 font-semibold text-sm sm:text-base">Account Created:</strong>
                  <span className="text-gray-600 dark:text-gray-300">
                    {user?.created_at 
                      ? new Date(user.created_at).toLocaleDateString()
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-gray-800 dark:text-white text-xl font-semibold mb-5 pb-3 border-b-2 border-gray-200 dark:border-gray-700">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
                <button
                  onClick={() => navigate('/profile')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">📝</span>
                  <span className="hidden sm:inline">Update Profile</span>
                  <span className="sm:hidden">Profile</span>
                </button>
                <button 
                  onClick={handleChangePassword}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">🔒</span>
                  <span className="hidden sm:inline">Change Password</span>
                  <span className="sm:hidden">Password</span>
                </button>
                <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg">
                  <span className="text-base sm:text-lg">📊</span>
                  <span className="hidden sm:inline">View Reports</span>
                  <span className="sm:hidden">Reports</span>
                </button>
                <button
                  onClick={() => navigate('/customers')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">👥</span>
                  <span className="hidden sm:inline">Customers</span>
                  <span className="sm:hidden">Customers</span>
                </button>
                <button
                  onClick={() => navigate('/customer-contacts')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">📋</span>
                  <span className="hidden sm:inline">Customer Contacts</span>
                  <span className="sm:hidden">Contacts</span>
                </button>
                <button
                  onClick={() => navigate('/sites')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">🏢</span>
                  <span className="hidden sm:inline">Sites</span>
                  <span className="sm:hidden">Sites</span>
                </button>
                <button
                  onClick={() => navigate('/site-contacts')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">📌</span>
                  <span className="hidden sm:inline">Site Contacts</span>
                  <span className="sm:hidden">Site Contacts</span>
                </button>
                <button
                  onClick={() => navigate('/jobs')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">🔧</span>
                  <span className="hidden sm:inline">Jobs</span>
                  <span className="sm:hidden">Jobs</span>
                </button>
                <button
                  onClick={() => navigate('/vat-rates')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">💰</span>
                  <span className="hidden sm:inline">VAT Rates</span>
                  <span className="sm:hidden">VAT</span>
                </button>
                <button
                  onClick={() => navigate('/invoices')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">🧰</span>
                  <span className="hidden sm:inline">Invoices</span>
                  <span className="sm:hidden">Invoices</span>
                </button>
                <button
                  onClick={() => navigate('/vehicles')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">🚗</span>
                  <span className="hidden sm:inline">Vehicles</span>
                  <span className="sm:hidden">Vehicles</span>
                </button>
                <button
                  onClick={() => navigate('/journeys')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">🗺️</span>
                  <span className="hidden sm:inline">Journeys</span>
                  <span className="sm:hidden">Journeys</span>
                </button>
                <button
                  onClick={() => navigate('/estimates')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">📄</span>
                  <span className="hidden sm:inline">Estimates</span>
                  <span className="sm:hidden">Estimates</span>
                </button>
                <button
                  onClick={() => navigate('/expenses')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">💸</span>
                  <span className="hidden sm:inline">Expenses</span>
                  <span className="sm:hidden">Expenses</span>
                </button>
                <button
                  onClick={() => navigate('/tasks')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">✅</span>
                  <span className="hidden sm:inline">Tasks</span>
                  <span className="sm:hidden">Tasks</span>
                </button>
                <button
                  onClick={() => navigate('/documents')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">📁</span>
                  <span className="hidden sm:inline">Documents</span>
                  <span className="sm:hidden">Documents</span>
                </button>
                <button
                  onClick={() => navigate('/task-sets')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-lg p-3 sm:p-4 text-xs sm:text-sm font-medium cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:transform hover:scale-105 hover:shadow-lg"
                >
                  <span className="text-base sm:text-lg">📋</span>
                  <span className="hidden sm:inline">Task Sets</span>
                  <span className="sm:hidden">Task Sets</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;